import { useState } from "react";
import { useI18n } from "@/i18n";
import { loungeCopy } from "@/lib/loungeCopy";
import { resolveLoungeUrl } from "@/lib/loungeUrl";
import api from "@/lib/api";
import { trackLoungeEvent } from "@/lib/loungeTrack";
import { LoungeSectionTitle } from "@/components/lounge/LoungeShell";
import { Button } from "@/components/ui/button";
import { Play, Eye, Download, X, Film, FileText } from "lucide-react";
import { usePressGate, triggerDownload } from "@/components/lounge/PressAccreditationGate";

const DOC_BADGE = { it: "Documento", en: "Document", fr: "Document", de: "Dokument", es: "Documento" };
const DOWNLOAD_PDF = { it: "Scarica PDF", en: "Download PDF", fr: "Télécharger le PDF", de: "PDF herunterladen", es: "Descargar PDF" };

const slug = (s) => (s || "").replace(/[^a-z0-9]+/gi, "-").toLowerCase();

// Aggiunge ?download=1 ai file serviti dal nostro storage per forzare il download hi-res.
function downloadHref(url) {
  const u = resolveLoungeUrl(url);
  if (!u) return "";
  if (u.includes("/api/public/lounge-file/")) return `${u}${u.includes("?") ? "&" : "?"}download=1`;
  return u;
}

// Libreria multimediale pubblica: card foto/video con "Vedi anteprima" (lightbox / player)
// e "Scarica" (file originale hi-res). Fallback poster pulito per i video senza copertina.
export function LoungeMediaLibrary({ media }) {
  const { lang } = useI18n();
  const c = loungeCopy(lang);
  const { gate, area } = usePressGate();
  const [preview, setPreview] = useState(null); // { type:'image'|'video', src, title }
  const registerHit = (id) => { if (id) api.post(`/public/lounge/media/${id}/download`).catch(() => {}); };

  const sections = Object.entries(media || {});
  if (sections.length === 0) return null;

  return (
    <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24" data-testid="lounge-media">
      <LoungeSectionTitle eyebrow="Assets" title={c.media_title} subtitle={c.media_sub} testId="lounge-media-title" />
      <div className="space-y-12">
        {sections.map(([section, items]) => (
          <div key={section} data-testid={`lounge-media-section-${section}`}>
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-brand">
              {(c.media_sections && c.media_sections[section]) || section}
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((m, j) => {
                const isVideo = m.kind === "video";
                const isDoc = m.kind === "document";
                const thumb = resolveLoungeUrl(m.image_url);
                const videoSrc = resolveLoungeUrl(m.video_url);
                const dl = downloadHref(m.download_url);
                const openPreview = () => {
                  const track = { type: "download", item: { kind: "media", id: m.id, title: m.title, filename: m.original_filename || "" } };
                  if (isDoc) { if (dl) gate(() => { registerHit(m.id); triggerDownload(dl); }, track); return; }
                  trackLoungeEvent({ area, type: "view", item: { kind: "media", id: m.id, title: m.title } });
                  if (isVideo && videoSrc) setPreview({ type: "video", src: videoSrc, title: m.title });
                  else if (thumb) setPreview({ type: "image", src: thumb, title: m.title });
                  else if (videoSrc) setPreview({ type: "video", src: videoSrc, title: m.title });
                };
                const tid = `${slug(section)}-${j}`;
                return (
                  <div key={j} className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card" data-testid={`lounge-media-card-${tid}`}>
                    <button
                      type="button"
                      onClick={openPreview}
                      className="relative block aspect-video w-full overflow-hidden bg-gradient-to-br from-muted to-card"
                      data-testid={`media-thumb-${tid}`}
                      aria-label={m.title || "media"}
                    >
                      {thumb ? (
                        <img src={thumb} alt={m.title || ""} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      ) : (
                        <span className="flex h-full w-full items-center justify-center text-muted-foreground/50">
                          {isDoc ? <FileText className="h-10 w-10" /> : <Film className="h-10 w-10" />}
                        </span>
                      )}
                      {isVideo && (
                        <span className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors group-hover:bg-black/35">
                          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background/90 text-foreground shadow-lg">
                            <Play className="h-6 w-6 translate-x-0.5" fill="currentColor" />
                          </span>
                        </span>
                      )}
                      {isDoc && (
                        <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded bg-brand px-1.5 py-0.5 text-[10px] font-bold uppercase text-white shadow">
                          <FileText className="h-3 w-3" /> PDF
                        </span>
                      )}
                    </button>

                    <div className="flex flex-1 flex-col p-4">
                      {isVideo && (
                        <span className="mb-1 inline-block w-fit rounded bg-brand/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-brand">{c.video_badge}</span>
                      )}
                      {isDoc && (
                        <span className="mb-1 inline-flex w-fit items-center gap-1 rounded bg-brand/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-brand"><FileText className="h-3 w-3" /> {DOC_BADGE[lang] || DOC_BADGE.it}</span>
                      )}
                      <div className="text-sm font-semibold text-foreground">{m.title}</div>
                      {m.description && <div className="mt-0.5 text-xs text-muted-foreground">{m.description}</div>}

                      <div className="mt-3 flex flex-wrap gap-2 pt-1">
                        {!isDoc && (
                          <Button type="button" size="sm" variant="secondary" onClick={openPreview} className="rounded-full" data-testid={`media-preview-btn-${tid}`}>
                            <Eye className="mr-1.5 h-3.5 w-3.5" /> {c.media_preview}
                          </Button>
                        )}
                        {dl && (
                          <Button size="sm" variant="outline" onClick={() => gate(() => { registerHit(m.id); triggerDownload(dl); }, { type: "download", item: { kind: "media", id: m.id, title: m.title, filename: m.original_filename || "" } })} className="rounded-full border-border bg-transparent text-foreground hover:bg-brema-hover" data-testid={`media-download-btn-${tid}`}>
                            {isDoc ? <FileText className="mr-1.5 h-3.5 w-3.5" /> : <Download className="mr-1.5 h-3.5 w-3.5" />} {isDoc ? (DOWNLOAD_PDF[lang] || DOWNLOAD_PDF.it) : c.media_download}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {preview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4" onClick={() => setPreview(null)} data-testid="media-preview-modal">
          {preview.type === "video" ? (
            <video src={preview.src} controls autoPlay onClick={(e) => e.stopPropagation()} className="max-h-[90vh] max-w-[92vw] rounded-lg bg-black shadow-2xl" data-testid="media-preview-video" />
          ) : (
            <img src={preview.src} alt={preview.title || ""} onClick={(e) => e.stopPropagation()} className="max-h-[92vh] max-w-[92vw] rounded-lg object-contain shadow-2xl" data-testid="media-preview-image" />
          )}
          <button type="button" onClick={(e) => { e.stopPropagation(); setPreview(null); }} data-testid="media-preview-close" className="absolute right-4 top-4 rounded-full bg-white/15 p-2 text-white transition hover:bg-white/30">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </section>
  );
}
